/**
 * @fileoverview Parsing helpers for document tags inside stored chat
 * messages (MOR-890).
 *
 * Sent user messages carry their document references as plain text:
 * `@FileName` for mentions typed in the composer, and a trailing
 * `Attached: a, b` line for picker-attached files. These helpers split
 * that text back apart so the message bubble can render chips instead
 * of raw text. Pure functions kept out of ChatMessage.tsx so they're
 * unit-testable.
 */

/** Marker `UniversalChatPanel.handleSend` appends for picker attachments. */
const ATTACHED_SUFFIX_MARKER = "\n\nAttached: ";

/**
 * Splits a stored user message into its typed body and the file names
 * from the trailing `Attached: …` line (empty when there is none).
 */
export function splitAttachmentSuffix(content: string): {
  body: string;
  attachedNames: string[];
} {
  const at = content.lastIndexOf(ATTACHED_SUFFIX_MARKER);
  if (at === -1) return { body: content, attachedNames: [] };
  const suffix = content.slice(at + ATTACHED_SUFFIX_MARKER.length);
  // The suffix is a single line — a newline after it means the marker
  // was part of the user's own text, not our appended footer.
  if (suffix.includes("\n") || suffix.trim().length === 0) {
    return { body: content, attachedNames: [] };
  }
  return {
    body: content.slice(0, at).trimEnd(),
    attachedNames: suffix
      .split(", ")
      .map((name) => name.trim())
      .filter((name) => name.length > 0),
  };
}

export type MessageTagSegment =
  { type: "text"; text: string } | { type: "mention"; name: string };

/**
 * Splits message text into prose and `@FileName` mention segments by
 * matching against the known document names. Only an `@` that starts a
 * word (start-of-text or after whitespace) can open a mention — same
 * rule as the composer's `findActiveMention`. Longest name wins so
 * files whose names prefix each other resolve correctly, and names may
 * contain spaces.
 */
export function segmentMentions(
  text: string,
  knownNames: readonly string[],
): MessageTagSegment[] {
  if (text.length === 0) return [];
  const names = [...knownNames].sort((a, b) => b.length - a.length);
  const segments: MessageTagSegment[] = [];
  let plainStart = 0;
  let i = 0;
  while (i < text.length) {
    const isWordStart = i === 0 || /\s/.test(text[i - 1]);
    if (text[i] === "@" && isWordStart) {
      const match = names.find((name) => text.startsWith(name, i + 1));
      if (match) {
        if (i > plainStart) {
          segments.push({ type: "text", text: text.slice(plainStart, i) });
        }
        segments.push({ type: "mention", name: match });
        i += 1 + match.length;
        plainStart = i;
        continue;
      }
    }
    i++;
  }
  if (plainStart < text.length) {
    segments.push({ type: "text", text: text.slice(plainStart) });
  }
  return segments;
}
